import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import PropTypes from 'prop-types';
import { WoodIcon, GlassIcon, CamouflageIcon, DenimIcon } from '@sainsburys-tech/bolt-icons';
import FabricTileSwatch from './FabricTileSwatch/FabricTileSwatch';
import { WOODS, GLASS, METALS, CAMOUFLAGE, DENIM } from '../config/config';
import * as Styled from '../ColorSwatch.styles';

var labelToId = function labelToId(color) {
  return color.trim().toLowerCase().replace(/ /gi, '-');
};

var getSwatch = function getSwatch(id, selected, size, swatchAnchorLink, dataTest) {
  var isMetal = METALS.indexOf(id) > -1;
  var classId = isMetal ? 'metal' : 'color';

  var _ref = size === 'medium' ? ['24px', '24px'] : ['18px', '18px'],
      _ref2 = _slicedToArray(_ref, 2),
      width = _ref2[0],
      height = _ref2[1];

  switch (true) {
    case id === 'multi':
      return null;

    case id === 'multicoloured':
    case isMetal:
      return /*#__PURE__*/React.createElement(Styled.Swatch, {
        color: id,
        size: size,
        selected: selected,
        key: id,
        role: !swatchAnchorLink ? 'button' : undefined,
        "aria-label": !swatchAnchorLink ? id : undefined,
        tabIndex: !swatchAnchorLink ? '0' : undefined,
        "data-test": "".concat(dataTest, "-").concat(id, "-button"),
        "aria-selected": selected
      }, /*#__PURE__*/React.createElement(Styled.Wheel, {
        size: size,
        classId: classId
      }, /*#__PURE__*/React.createElement("div", {
        className: "blur"
      }, /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }), /*#__PURE__*/React.createElement("div", {
        className: "slice ".concat(classId)
      }))));

    default:
      return /*#__PURE__*/React.createElement(Styled.Swatch, {
        color: id,
        size: size,
        selected: selected,
        key: id,
        role: !swatchAnchorLink ? 'button' : undefined,
        "aria-label": !swatchAnchorLink ? id : undefined,
        tabIndex: !swatchAnchorLink ? '0' : undefined,
        "data-test": "".concat(dataTest, "-").concat(id, "-button"),
        "aria-selected": selected
      }, WOODS.indexOf(id) > -1 ? /*#__PURE__*/React.createElement(WoodIcon, {
        width: width,
        height: height,
        className: "effect ".concat(size)
      }) : null, GLASS.indexOf(id) > -1 ? /*#__PURE__*/React.createElement(GlassIcon, {
        width: width,
        height: height,
        className: "effect ".concat(size)
      }) : null, CAMOUFLAGE.indexOf(id) > -1 ? /*#__PURE__*/React.createElement(CamouflageIcon, {
        width: width,
        height: height,
        className: "effect ".concat(size, " ").concat(id)
      }) : null, DENIM.indexOf(id) > -1 ? /*#__PURE__*/React.createElement(DenimIcon, {
        width: width,
        height: height,
        className: "effect ".concat(size)
      }) : null);
  }
};

var ColorSwatchOption = function ColorSwatchOption(_ref3) {
  var color = _ref3.color,
      fabric = _ref3.fabric,
      altLabel = _ref3.altLabel,
      hideLabel = _ref3.hideLabel,
      selected = _ref3.selected,
      swatchAnchorLink = _ref3.swatchAnchorLink,
      size = _ref3.size,
      isFabricSwatches = _ref3.isFabricSwatches,
      className = _ref3.className,
      dataTest = _ref3['data-test'];
  var label = isFabricSwatches ? "".concat(color, " ").concat(fabric) : color;
  var id = labelToId(label);
  return /*#__PURE__*/React.createElement(Styled.ColorSwatchOptions, {
    hideLabel: hideLabel,
    className: className,
    "data-test": dataTest
  }, isFabricSwatches ? /*#__PURE__*/React.createElement(FabricTileSwatch, {
    color: color,
    fabric: fabric,
    id: id,
    size: size,
    swatchAnchorLink: swatchAnchorLink,
    "data-test": dataTest,
    selected: selected
  }) : id.split('-and-').map(function (singleColor) {
    return getSwatch(singleColor, selected, size, swatchAnchorLink, dataTest);
  }), /*#__PURE__*/React.createElement("span", {
    className: hideLabel ? 'sr-only' : 'color-label'
  }, altLabel || label));
};

ColorSwatchOption.propTypes = {
  /**
   * Specify the color id
   */
  color: PropTypes.string.isRequired,

  /**
   * Optional prop for the fabric type when displaying as fabric swatches
   */
  fabric: PropTypes.string,

  /**
   * Optional prop to specify the label name
   */
  altLabel: PropTypes.string,

  /**
   * Optional prop to select the color
   */
  selected: PropTypes.bool,

  /**
   * Optional prop to remove button role, tabIndex and aria-label attributes
   */
  swatchAnchorLink: PropTypes.bool,

  /**
   * Optional prop to hide the label
   */
  hideLabel: PropTypes.bool,

  /**
   * Optional prop to change size of the circle (small, medium)
   */
  size: PropTypes.oneOf(['small', 'medium']),

  /**
   * Specify an optional className to be added to your Alert
   */
  className: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added for e2e testing
   */
  'data-test': PropTypes.string
};
ColorSwatchOption.defaultProps = {
  selected: false,
  hideLabel: false,
  showMoreIcon: false,
  size: 'small',
  'data-test': 'component-color-swatch'
};
export default ColorSwatchOption;